import { httpsCallable, httpsCallableFromURL } from "firebase/functions";
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db, functions, gatewayUrl } from "./firebase";
import type {
  CallLog,
  CatalogProvider,
  Connection,
  ConnectionScope,
  Invite,
  Project,
  ProviderResource,
  ServerDoc,
  StoredFile,
  Team,
} from "./types";

/* ------------------------------------------------------------------ *
 * Callables (mutations + anything needing secrets)
 * ------------------------------------------------------------------ */

/**
 * A callable whose own Cloud Run service is missing its public invoker binding --
 * deploys run as an account that cannot grant it, so the browser's CORS preflight
 * answers 403 and the call dies before our code runs. These go through the gateway on
 * the `oauth` function instead (functions/src/api/gateway.ts), which has its binding
 * and speaks the same callable protocol: same auth, same error codes, same messages.
 */
const gatewayCallable = <Req, Res>(name: string) =>
  httpsCallableFromURL<Req, Res>(functions, gatewayUrl(name));

/**
 * The dashboard's first call: makes sure the personal team and default project exist,
 * and migrates any pre-project server and connections into them -- without touching the
 * server's id or token. Returns the defaults so the UI has somewhere to land.
 */
export const ensureWorkspace = httpsCallable<
  void,
  { teamId: string; projectId: string }
>(functions, "ensureWorkspace");

// "teamCreate" server-side: the original createTeam function was deployed without its
// public invoker binding and could not be repaired in place -- see functions/src/index.ts.
export const createTeam = httpsCallable<
  { name: string },
  { teamId: string; projectId: string }
>(functions, "teamCreate");

export const joinTeam = httpsCallable<
  { code: string },
  { teamId: string; name: string }
>(functions, "joinTeam");

export const leaveTeam = httpsCallable<{ teamId: string }, { ok: boolean }>(
  functions,
  "leaveTeam"
);

/**
 * Invite an email address into a team. The invite shows up live in that person's
 * dashboard the moment they are signed in with the address; no email is dispatched.
 */
export const inviteToTeam = httpsCallable<
  { teamId: string; email: string },
  { ok: boolean; inviteId: string }
>(functions, "inviteToTeam");

/** Accept or decline an invitation addressed to the signed-in email. */
export const respondToInvite = httpsCallable<
  { inviteId: string; accept: boolean },
  { ok: boolean; teamId: string | null; teamName: string | null }
>(functions, "respondToInvite");

/** Withdraw a pending invite. Any member of the inviting team may. */
export const revokeInvite = httpsCallable<{ inviteId: string }, { ok: boolean }>(
  functions,
  "revokeInvite"
);

/** Owner-only. */
export const renameTeam = httpsCallable<
  { teamId: string; name: string },
  { ok: boolean }
>(functions, "renameTeam");

/** Owner-only: grant or revoke another member's ownership. */
export const setTeamOwner = httpsCallable<
  { teamId: string; memberUid: string; owner: boolean },
  { ok: boolean }
>(functions, "setTeamOwner");

/**
 * Owner-only. Deletes the team's projects and, under them, every member's server,
 * token and OAuth grant, and every connection and stored credential.
 */
export const deleteTeam = httpsCallable<{ teamId: string }, { ok: boolean }>(
  functions,
  "deleteTeam"
);

export const createProject = httpsCallable<
  { teamId: string; name: string },
  { projectId: string }
>(functions, "createProject");

/**
 * Rename a project. Any member may. Servers still carrying the old name follow it, so
 * the title an MCP client shows keeps matching the project -- unless its owner renamed
 * it by hand, in which case theirs is left alone.
 */
export const renameProject = gatewayCallable<
  { projectId: string; name: string },
  { ok: boolean }
>("renameProject");

/**
 * Delete a project, every member's server and setup key for it, and every connection
 * and stored credential in it. Owner-only, and irreversible.
 */
export const deleteProject = gatewayCallable<{ projectId: string }, { ok: boolean }>(
  "deleteProject"
);

/** Move a project (connections and all) to another team. Owner-only on the source side. */
export const moveProject = httpsCallable<
  { projectId: string; teamId: string },
  { ok: boolean; teamName: string }
>(functions, "moveProject");

/**
 * The caller's server for one project -- their own MCP URL and setup key there, created
 * on first ask. Every member of a project has their own, which is what keeps personal
 * connections (and their credentials) out of teammates' clients.
 */
export const ensureServer = httpsCallable<
  { projectId?: string },
  {
    serverId: string;
    projectId: string;
    url: string;
    token: string | null;
    /**
     * The server's pre-registered OAuth client, for MCP client configs that take a
     * pasted client id + secret (claude.ai's custom connector advanced settings)
     * instead of registering themselves.
     */
    oauthClientId: string;
    oauthClientSecret: string;
    created: boolean;
  }
>(functions, "ensureServer");

export const rotateToken = httpsCallable<
  { projectId: string },
  { serverId: string; url: string; token: string }
>(functions, "rotateToken");

export const renameServer = httpsCallable<
  { projectId: string; name: string },
  { ok: boolean }
>(functions, "renameServer");

/**
 * Switch one connected account on or off. Per-member for a personal connection (writes
 * the caller's own server), team-wide for a team one (writes the connection's shared
 * switch -- flipping it flips it for every member at once).
 */
export const setConnectionEnabled = gatewayCallable<
  { connectionId: string; enabled: boolean },
  { ok: boolean; enabled: boolean; scope: ConnectionScope }
>("setConnectionEnabled");

/** Same split as the switch: a team connection's allow-list is shared by the team. */
export const setEnabledTools = httpsCallable<
  { connectionId: string; tools: string[] | null },
  { ok: boolean }
>(functions, "setEnabledTools");

/** Rename a connection, and with it every tool it exposes. */
export const renameConnection = httpsCallable<
  { connectionId: string; slug: string },
  { ok: boolean; slug: string }
>(functions, "renameConnection");

/**
 * Start an OAuth redirect. Pass `connectionId` to re-authorise an account that already
 * exists; omit it to add another one.
 */
export const startConnect = httpsCallable<
  {
    providerId: string;
    methodId?: string;
    connectionId?: string;
    /** Where a *new* connection lands. Ignored on reconnect: a repair cannot move. */
    projectId?: string;
    scope?: ConnectionScope;
  },
  { url: string }
>(functions, "startConnect");

/** Connect with credentials the user pasted (a Slack webhook URL). No redirect. */
export const connectManual = httpsCallable<
  {
    providerId: string;
    methodId: string;
    values: Record<string, string>;
    connectionId?: string;
    projectId?: string;
    scope?: ConnectionScope;
  },
  {
    ok: boolean;
    connectionId: string;
    providerId: string;
    accountLabel: string;
    slug: string;
  }
>(functions, "connectManual");

export const disconnect = httpsCallable<{ connectionId: string }, { ok: boolean }>(
  functions,
  "disconnect"
);

/**
 * Share a copy of one of your connections with a team: a new team-level connection in
 * one of that team's projects, credential included. The original stays where it is and
 * the two are independent afterwards. Owner-only -- the copy serves the whole
 * destination team through your credential.
 */
export const copyConnection = gatewayCallable<
  { connectionId: string; projectId: string },
  {
    ok: boolean;
    connectionId: string;
    /** The copy's tool-name prefix in the destination project. */
    slug: string;
    teamName: string;
    projectName: string;
  }
>("copyConnection");

/**
 * Move one of your private connections to team level, in place: same document, same
 * credential, same project -- every member's server there starts serving its tools,
 * switched off for them until they choose otherwise. Owner-only. `renamed` is true when
 * the slug clashed with something else in the project and had to change; your server's
 * switch and allow-list follow it automatically.
 *
 * Via the gateway: renaming the function to "connectionShare" did not restore the
 * invoker binding either -- no deploy from CI can (see functions/src/index.ts).
 */
export const shareConnection = gatewayCallable<
  { connectionId: string },
  { ok: boolean; slug: string; renamed: boolean; teamName: string }
>("connectionShare");

/**
 * Re-read a connected MCP server's tool list. Only meaningful for a provider whose tools
 * come from the account (Generic MCP) -- the server it points at can gain and lose tools
 * whenever its owner deploys, and nothing tells us when.
 */
export const refreshTools = httpsCallable<
  { connectionId: string },
  // `offered`: how many the account has; `tools`: how many fit in storage. Absent on a
  // backend from before the distinction existed.
  { ok: boolean; tools: number; offered?: number }
>(functions, "refreshTools");

/**
 * What the repository picker renders: the resources a connection's credential can reach
 * (GitHub: repositories), with what is currently selected. Fetched on open, not watched
 * -- it comes from the provider, not from Firestore. Via the gateway: a new callable
 * deployed from CI never gets its public invoker binding (see functions/src/index.ts).
 */
export const listConnectionResources = gatewayCallable<
  { connectionId: string },
  {
    noun: string;
    hint: string;
    resources: ProviderResource[];
    /** null = never narrowed: everything the credential can reach. */
    selected: string[] | null;
  }
>("listConnectionResources");

/**
 * Narrow a connection to specific resources, or widen it back out with null. Same team
 * semantics as the tool allow-list: on a team connection the selection is shared.
 */
export const setAllowedResources = gatewayCallable<
  { connectionId: string; resources: string[] | null },
  { ok: boolean; selected: string[] | null }
>("setAllowedResources");

/**
 * Everything the project's tools have generated into Cloud Storage.
 *
 * A fetch rather than a listener, and deliberately: these files live in a bucket, not in
 * Firestore, so there is nothing to subscribe to -- and the alternative, mirroring them
 * into a collection, buys a live list at the price of rows that outlive the objects
 * retention deletes. Via the gateway, like every callable added since the IAM breakage.
 */
export const listGeneratedFiles = gatewayCallable<
  { projectId: string },
  { retentionDays: number; files: StoredFile[] }
>("listGeneratedFiles");

/** Delete one stored file now, rather than waiting for retention to reach it. */
export const deleteGeneratedFile = gatewayCallable<
  { connectionId: string; name: string },
  { ok: boolean }
>("deleteGeneratedFile");

export const getCatalog = httpsCallable<void, { providers: CatalogProvider[] }>(
  functions,
  "getCatalog"
);

/**
 * Natural-language search over every connected account's tools.
 *
 * The catalog is assembled server-side and sent to Gemini; what comes back is a ranked
 * subset of tool ids (`github_acme__list_issues`) with a line each on why. The page already
 * has the tools themselves, so an id is all it needs to render and toggle the row.
 */
export const searchTools = httpsCallable<
  { projectId: string; query: string },
  {
    matches: { id: string; reason: string }[];
    summary: string;
    /** How many tools were searched -- shown so an empty result is legible. */
    indexed: number;
  }
>(functions, "searchTools");

/* ------------------------------------------------------------------ *
 * OAuth consent -- the /consent page, where an MCP client that connected by OAuth
 * (Claude Code in Anthropic's cloud) is approved or denied.
 * ------------------------------------------------------------------ */

export const getOauthRequest = httpsCallable<
  { requestId: string },
  {
    clientName: string;
    redirectHost: string;
    scope: string | null;
    resource: string | null;
    /** The project of the server the client's MCP URL names, when it is the caller's. */
    projectId: string | null;
  }
>(functions, "getOauthRequest");

/** Returns where to send the browser: the client's redirect_uri, with a code or a denial. */
export const decideOauthRequest = httpsCallable<
  /** projectId picks which of the user's servers the client is authorized against. */
  { requestId: string; approve: boolean; projectId?: string },
  { redirectTo: string }
>(functions, "decideOauthRequest");

/* ------------------------------------------------------------------ *
 * Live reads (Firestore listeners, so the UI reflects reality without polling)
 * ------------------------------------------------------------------ */

/** Every team the user belongs to. Provable against the rules via array-contains. */
export function watchTeams(uid: string, cb: (teams: Team[]) => void) {
  const q = query(
    collection(db, "teams"),
    where("memberUids", "array-contains", uid)
  );
  return onSnapshot(q, (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Team, "id">) })));
  });
}

/**
 * Every project of every given team. One listener per team, because the read rule
 * resolves membership from the query's teamId constraint -- and because `in` caps at a
 * handful of values while a listener per team scales with the number of teams, which
 * is small by nature.
 */
export function watchProjects(teamIds: string[], cb: (projects: Project[]) => void) {
  if (teamIds.length === 0) {
    cb([]);
    return () => {};
  }

  const byTeam = new Map<string, Project[]>();
  const emit = () => cb([...byTeam.values()].flat());

  const unsubs = teamIds.map((teamId) =>
    onSnapshot(
      query(collection(db, "projects"), where("teamId", "==", teamId)),
      (snap) => {
        byTeam.set(
          teamId,
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Project, "id">) }))
        );
        emit();
      },
      // A team the user was just removed from starts erroring; drop its projects.
      () => {
        byTeam.delete(teamId);
        emit();
      }
    )
  );
  return () => unsubs.forEach((u) => u());
}

/**
 * Invitations addressed to the signed-in email, live. This is what makes inviting
 * "live": the document is written by a teammate's callable and this listener puts it
 * on the invitee's screen in the same second, no refresh and no email round trip.
 */
export function watchIncomingInvites(email: string, cb: (invites: Invite[]) => void) {
  const q = query(
    collection(db, "invites"),
    where("email", "==", email.toLowerCase())
  );
  return onSnapshot(
    q,
    (snap) => {
      cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Invite, "id">) })));
    },
    () => cb([])
  );
}

/**
 * Outstanding invites of every given team, for the team cards. Same per-team listener
 * shape as watchProjects, and for the same rule-provability reason.
 */
export function watchTeamInvites(teamIds: string[], cb: (invites: Invite[]) => void) {
  if (teamIds.length === 0) {
    cb([]);
    return () => {};
  }

  const byTeam = new Map<string, Invite[]>();
  const emit = () => cb([...byTeam.values()].flat());

  const unsubs = teamIds.map((teamId) =>
    onSnapshot(
      query(collection(db, "invites"), where("teamId", "==", teamId)),
      (snap) => {
        byTeam.set(
          teamId,
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Invite, "id">) }))
        );
        emit();
      },
      () => {
        byTeam.delete(teamId);
        emit();
      }
    )
  );
  return () => unsubs.forEach((u) => u());
}

/** The caller's own server for one project -- the one whose key the Setup page shows. */
export function watchServer(
  uid: string,
  projectId: string,
  cb: (server: (ServerDoc & { id: string }) | null) => void
) {
  const q = query(
    collection(db, "servers"),
    where("ownerUid", "==", uid),
    where("projectId", "==", projectId),
    limit(1)
  );
  return onSnapshot(q, (snap) => {
    if (snap.empty) {
      cb(null);
      return;
    }
    const doc = snap.docs[0];
    cb({ id: doc.id, ...(doc.data() as ServerDoc) });
  });
}

/**
 * What one member sees in one project: the team-level connections plus their own
 * personal ones. Two listeners because they are two different rule paths -- the
 * personal query is provable by ownership, the team one by membership -- merged here
 * so the pages get one array, exactly as the backend's `listProjectConnections` builds.
 */
export function watchProjectConnections(
  uid: string,
  project: { id: string; teamId: string },
  cb: (conns: Connection[]) => void
) {
  const halves = new Map<string, Connection[]>();
  const emit = () => {
    const seen = new Map<string, Connection>();
    for (const conn of [...halves.values()].flat()) seen.set(conn.id, conn);
    cb([...seen.values()]);
  };

  const listen = (key: string, q: ReturnType<typeof query>) =>
    onSnapshot(
      q,
      (snap) => {
        halves.set(
          key,
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Connection, "id">) }))
        );
        emit();
      },
      () => {
        halves.set(key, []);
        emit();
      }
    );

  const unsubs = [
    listen(
      "personal",
      query(
        collection(db, "connections"),
        where("ownerUid", "==", uid),
        where("projectId", "==", project.id)
      )
    ),
    listen(
      "team",
      query(
        collection(db, "connections"),
        where("teamId", "==", project.teamId),
        where("projectId", "==", project.id),
        where("scope", "==", "team")
      )
    ),
  ];
  return () => unsubs.forEach((u) => u());
}

/**
 * One-shot read of the user's teams and projects, for pages outside the dashboard
 * shell (the consent screen) that need the list once and will navigate away.
 */
export async function listWorkspace(
  uid: string
): Promise<{ teams: Team[]; projects: Project[] }> {
  const teamsSnap = await getDocs(
    query(collection(db, "teams"), where("memberUids", "array-contains", uid))
  );
  const teams = teamsSnap.docs.map(
    (d) => ({ id: d.id, ...(d.data() as Omit<Team, "id">) })
  );

  const projectSnaps = await Promise.all(
    teams.map((t) =>
      getDocs(query(collection(db, "projects"), where("teamId", "==", t.id)))
    )
  );
  const projects = projectSnaps.flatMap((snap) =>
    snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Project, "id">) }))
  );
  return { teams, projects };
}

export function watchActivity(
  uid: string,
  projectId: string,
  cb: (logs: CallLog[]) => void
) {
  const q = query(
    collection(db, "callLogs"),
    where("ownerUid", "==", uid),
    where("projectId", "==", projectId),
    orderBy("ts", "desc"),
    limit(25)
  );
  return onSnapshot(
    q,
    (snap) => {
      cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CallLog, "id">) })));
    },
    // The composite index for this query may not exist on a fresh project. An empty
    // activity feed is a much better failure than a crashed dashboard.
    () => cb([])
  );
}

/** Firebase callables wrap errors; pull out something a human can read. */
export function errorMessage(err: unknown): string {
  if (typeof err !== "object" || err === null || !("message" in err)) {
    return "Something went wrong.";
  }

  const { message, code, details } = err as {
    message: unknown;
    code?: unknown;
    details?: unknown;
  };
  let text = String(message).trim();

  // e.g. "functions/deadline-exceeded" -> "deadline-exceeded". The SDK's own codes
  // ("auth/...", "functions/...") often say more than the message does.
  const shortCode =
    typeof code === "string" ? code.replace(/^functions\//, "") : "";

  // A bare "internal" is the SDK's stand-in for every failure the function never
  // explained: it crashed before saying why, the request never reached it (network,
  // CORS), or the service refused it before our code ran (a missing IAM binding
  // answers 403 and the SDK reports "internal"). Callables wrap their own errors in
  // readable messages, so by the time this shows, the code is all we have -- name the
  // possibilities rather than echo the one unhelpful word.
  if (!text || /^internal$/i.test(text)) {
    text =
      "The server hit an unexpected error, or could not be reached at all. " +
      "Try again in a moment; if it keeps happening, the function logs will say more.";
  } else if (
    shortCode &&
    shortCode !== "internal" &&
    !text.toLowerCase().includes(shortCode.toLowerCase())
  ) {
    // Keep the human-readable message first, but carry the code -- it is what a bug
    // report or a support search actually needs.
    text += ` (${shortCode})`;
  }

  // HttpsError can carry structured details; when they are text, they are meant to
  // be read.
  if (typeof details === "string" && details.trim()) {
    text += ` — ${details.trim()}`;
  }

  return text;
}
